import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useAgenticSearchController } from '../features/agentic-search/useAgenticSearchController';
import type { AgenticStoreController } from '../features/agentic-search/types';
import { useAuth } from './AuthContext';
import { useTabState } from './TabStateContext';

interface AgenticSearchWorkspaceContextValue {
  controller: AgenticStoreController;
  isActivated: boolean;
  activate: () => void;
}

const AgenticSearchWorkspaceContext = createContext<AgenticSearchWorkspaceContextValue | null>(null);

interface ActivatedControllerOwnerProps {
  userId: string | null;
  isActive: boolean;
  selectedIndexes: string[] | null;
  selectedRunIds: string[] | null;
  initialActiveSessionId: string | null;
  onController: (controller: AgenticStoreController) => void;
}

const ActivatedControllerOwner: React.FC<ActivatedControllerOwnerProps> = ({
  userId,
  isActive,
  selectedIndexes,
  selectedRunIds,
  initialActiveSessionId,
  onController,
}) => {
  const controller = useAgenticSearchController({
    userId,
    isActive,
    selectedIndexes,
    selectedRunIds,
    initialActiveSessionId,
  });
  useLayoutEffect(() => {
    onController(controller);
  }, [controller, onController]);

  return null;
};

/**
 * Owns the agent controller above the route switch. The controller stays alive
 * while the signed-in application is mounted, so navigating away from Search
 * cannot discard a warm session snapshot. Durable turn observation pauses
 * when its view is hidden; execution remains owned by the backend. Network
 * and IndexedDB work remain gated until Agent is explicitly opened.
 */
export const AgenticSearchWorkspaceProvider: React.FC<{ children: ReactNode; isActive?: boolean }> = ({ children, isActive = true }) => {
  const { currentUser } = useAuth();
  const { searchTabState } = useTabState();
  const [isActivated, setIsActivated] = useState(false);
  const [controller, setController] = useState<AgenticStoreController | null>(null);
  const agentState = searchTabState.agenticSearch;

  const activate = useCallback(() => {
    setIsActivated(true);
  }, []);

  const userId = currentUser?.uid ?? null;
  // Activation and authentication are lifecycle boundaries. A keyed owner
  // starts the controller directly in its final auth scope, avoiding an
  // initial null-user effect that could invalidate the first session load.
  // Index/run changes do not affect the key, so active streams stay alive.
  const ownerKey = `${userId || '__signed_out__'}:${isActivated ? 'active' : 'idle'}`;
  const value = useMemo<AgenticSearchWorkspaceContextValue | null>(() => (
    controller
      ? { controller, isActivated, activate }
      : null
  ), [activate, controller, isActivated]);

  return (
    <>
      <ActivatedControllerOwner
        key={ownerKey}
        userId={isActivated ? userId : null}
        isActive={isActive}
        selectedIndexes={isActivated ? agentState.selectedIndexes : null}
        selectedRunIds={isActivated ? agentState.selectedRunIds : null}
        initialActiveSessionId={isActivated ? agentState.sessionId : null}
        onController={setController}
      />
      {value && (
        <AgenticSearchWorkspaceContext.Provider value={value}>
          {children}
        </AgenticSearchWorkspaceContext.Provider>
      )}
    </>
  );
};

export const useAgenticSearchWorkspace = (): AgenticSearchWorkspaceContextValue => {
  const value = useContext(AgenticSearchWorkspaceContext);
  if (!value) {
    throw new Error('useAgenticSearchWorkspace must be used within AgenticSearchWorkspaceProvider');
  }
  return value;
};
